import { Component } from '@theme/component';

/**
 * DAVIDsTEA announcement bar: cycles through the message blocks.
 *
 * The section paints every message in the same grid cell and marks the first one active, so
 * the bar reads correctly before this runs and keeps reading correctly if it never does. All
 * this adds is which one carries `data-active`.
 *
 * The cycle stops while a pointer is over the bar or a link inside it holds focus — a moving
 * target cannot be clicked, and an auto-updating region needs a way to be paused. It also
 * stops in a background tab, where nothing is read anyway.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} messages
 *
 * @extends {Component<Refs>}
 */
export class DtAnnouncement extends Component {
  requiredRefs = ['messages'];

  /** @type {number | undefined} */
  #timer;
  #index = 0;
  #held = false;

  connectedCallback() {
    super.connectedCallback();

    // One message is not a rotation.
    if (this.refs.messages.length < 2) return;

    this.addEventListener('pointerenter', this.#hold);
    this.addEventListener('pointerleave', this.#release);
    this.addEventListener('focusin', this.#hold);
    this.addEventListener('focusout', this.#release);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #start() {
    clearInterval(this.#timer);
    const interval = Number(this.dataset.interval) || 5000;
    this.#timer = setInterval(() => this.#advance(), interval);
  }

  #advance() {
    const { messages } = this.refs;
    this.#index = (this.#index + 1) % messages.length;

    messages.forEach((message, i) => {
      const active = i === this.#index;
      message.toggleAttribute('data-active', active);
      // The messages sit on top of one another, so the ones behind have to leave both the
      // accessibility tree and the tab order, links included.
      message.setAttribute('aria-hidden', String(!active));
    });
  }

  #hold = () => {
    this.#held = true;
    clearInterval(this.#timer);
  };

  #release = (/** @type {Event} */ event) => {
    // focusout fires while moving between two links of the same message: only release when
    // the focus has actually left the bar.
    if (event instanceof FocusEvent && event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) {
      return;
    }

    this.#held = false;
    if (!document.hidden) this.#start();
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      clearInterval(this.#timer);
    } else if (!this.#held) {
      this.#start();
    }
  };
}

if (!customElements.get('dt-announcement-component')) {
  customElements.define('dt-announcement-component', DtAnnouncement);
}
